import { Button, Card, Text, makeStyles, tokens } from '@fluentui/react-components';
import type { MenuItem } from '../types/catering';
import placeholderImage from '../mocks/assets/menu/executive-breakfast.png';
import { COMPACT_CONTROL_HEIGHT, COMPACT_CONTROL_RADIUS } from './controlStyles';
import { DietaryDots } from './DietaryDots';
import { PriceText } from './PriceText';
import { QuantityStepper } from './QuantityStepper';

const useStyles = makeStyles({
  card: {
    display: 'grid',
    gridTemplateColumns: '5rem 1fr',
    columnGap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    borderRadius: '0',
    boxShadow: 'none',
    borderLeft: '0',
    borderRight: '0',
  },
  image: {
    width: '5rem',
    height: '100%',
    minHeight: '5rem',
    alignSelf: 'stretch',
    display: 'block',
    objectFit: 'cover',
    borderRadius: tokens.borderRadiusMedium,
    gridRow: '1',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: '0',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: {
    padding: `${tokens.spacingVerticalS} 0 0`,
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
  },
  details: {
    width: 'fit-content',
    height: COMPACT_CONTROL_HEIGHT,
    minHeight: COMPACT_CONTROL_HEIGHT,
    paddingInline: tokens.spacingHorizontalM,
    borderRadius: COMPACT_CONTROL_RADIUS,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    fontSize: '0.625rem',
    lineHeight: '1rem',
  },
});

export type MenuItemCardProps = {
  item: MenuItem;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onDetails: () => void;
};

export function MenuItemCard({ item, quantity, onQuantityChange, onDetails }: MenuItemCardProps) {
  const styles = useStyles();
  return (
    <Card className={styles.card}>
      <img className={styles.image} src={item.imageUrl ?? placeholderImage} alt="" />
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <Text className={styles.title} weight="semibold">
            {item.name}
          </Text>
          <PriceText
            amount={item.priceInfo.grossUnitCosts.Delivery}
            size="small"
            weight="semibold"
            includeCurrency={false}
          />
        </div>
        <DietaryDots allergens={item.allergens} />
        <div className={styles.footer}>
          <Button className={styles.details} appearance="outline" size="small" onClick={onDetails}>
            More details
          </Button>
          <QuantityStepper
            value={quantity}
            min={item.minimumWhenInOrder}
            onChange={onQuantityChange}
            label={`${item.name} ${item.isMeasured ? 'units' : 'portions'} per person`}
          />
        </div>
      </div>
    </Card>
  );
}
